import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { IconFolder, IconLayout2, IconSettings, IconShoppingCart, IconCreditCard, IconSchool, IconChartBar, type Icon } from '@tabler/icons-react'
import StatSparkline from '@/components/Stats/StatSparkline.tsx'
import { usePortalContext } from '@/pages/portal/Portal.page.tsx'
import PortalStandardComponent from '@/components/portal/PortalStandardComponent.tsx'
import TutorialSpot from '@/components/tutorial/TutorialSpot.tsx'
import { useTutorial } from '@/contexts/tutorialContext.tsx'

interface DashboardLink {
  label: string
  description: string
  path: string
  icon: Icon
}

const dashboardLinks: DashboardLink[] = [
  {
    label: 'Projekte',
    description: 'Erstelle Galerien mit Fotos und Videos, die du deinen Kunden zur Verfügung stellst.',
    path: '/portal/projects',
    icon: IconFolder,
  },
  {
    label: 'Portfolios',
    description: 'Erstelle automatisch Portfolios aus deinen Projekten und teile sie, um für deine Arbeit zu werben.',
    path: '/portal/portfolios',
    icon: IconLayout2,
  },
  {
    label: 'Einstellungen',
    description: 'Verwalte deine Rechnungsadresse, Auszahlungsdaten und Kontaktinformationen.',
    path: '/portal/settings',
    icon: IconSettings,
  },
  {
    label: 'Shop & Provisionen',
    description: 'Verdiene Geld, wenn Kunden Fotos über deine Galerien bestellen. Konfiguriere Preise und behalte deine Provisionen im Blick.',
    path: '/portal/shop',
    icon: IconShoppingCart,
  },
  {
    label: 'Statistiken',
    description: 'Sieh, was in deinen Galerien passiert: Aufrufe, Downloads, geteilte Inhalte und App-Installationen.',
    path: '/portal/statistiken',
    icon: IconChartBar,
  },
  {
    label: 'Abonnement',
    description: 'Verwalte deinen Plan und deine Rechnungen.',
    path: '/portal/subscription',
    icon: IconCreditCard,
  },
  {
    label: 'Tutorials',
    description: 'Lerne Schritt für Schritt, wie du PicField nutzt.',
    path: '/portal/tutorials',
    icon: IconSchool,
  },
]

export default function DashboardPage() {
  const portalContext = usePortalContext()
  const isActive = useTutorial(s => s.isActive)
  const startTutorial = useTutorial(s => s.startTutorial)
  const isTutorialCompleted = useTutorial(s => s.isTutorialCompleted)

  // Auto-start tutorial on first visit if not completed
  useEffect(() => {
    if (!isActive && !isTutorialCompleted('getting-started')) {
      startTutorial('getting-started')
    }
  }, [])

  if (!portalContext?.account) {
    return <div>Kein Account geladen</div>
  }

  return (
    <PortalStandardComponent title="Willkommen zurück" noCard description='Verwalte deine Projekte, Portfolios und mehr'>
      {/* Last 14 days at a glance — each tile opens the full statistics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatSparkline group="views" label="Seitenaufrufe" to="/portal/statistiken" />
        <StatSparkline group="downloads" label="Downloads" to="/portal/statistiken" />
        <StatSparkline group="shares" label="Geteilt" to="/portal/statistiken" />
        <StatSparkline group="app_installs" label="Apps installiert" to="/portal/statistiken" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 auto-rows-fr">
        {dashboardLinks.map(link => {
          const card = (
            <Link
              key={link.path}
              to={link.path}
              className="bg-neutral p-6 flex gap-6 hover:shadow-md transition-shadow h-full border border-[var(--portal-border)]"
            >
              <link.icon size={28} className="text-primary flex-shrink-0 mt-0.5" />
              <div>
                <span className="text-xl font-serif">{link.label}</span>
                <p className="text-sm opacity-60 mt-1">{link.description}</p>
              </div>
            </Link>
          )
          if (link.path === '/portal/projects') {
            return <TutorialSpot key={link.path} id="dashboard-projects-card">{card}</TutorialSpot>
          }
          return card
        })}
      </div>
    </PortalStandardComponent>
  )
}
